import { AxiosInstance } from "axios";

// User interface matching backend response
export interface User {
  id: string;
  name: string;
  email: string;
  role: "physician" | "specialist" | "admin" | "super-admin";
  status: "pending" | "confirmed";
  profile?: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    specialization?: string;
    licenseNumber?: string;
    hospital?: string;
    bio?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

// Auth response interface
export interface AuthResponse {
  token: string;
  tokenExpires: string;
  user: User;
}

// API response wrapper
export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  error?: {
    code: string;
    message: string;
    details?: Array<{
      field: string;
      message: string;
    }>;
  };
  timestamp?: string;
  path?: string;
}

export class AuthApi {
  constructor(private api: AxiosInstance) {}

  // Register new user
  async signup(userData: {
    name: string;
    email: string;
    password: string;
  }): Promise<{ userId: string; email: string; name: string; status: string; role: string }> {
    try {
      const response = await this.api.post('/auth/register', userData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Registration failed');
    }
  }

  // Verify OTP
  async verifyOtp(email: string, otp: string): Promise<{ message: string }> {
    try {
      const response = await this.api.post('/auth/verify-otp', { email, otp });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'OTP verification failed');
    }
  }

  // Resend OTP
  async resendOtp(email: string): Promise<{}> {
    try {
      const response = await this.api.post('/auth/resend-otp', { email });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to resend OTP');
    }
  }

  // Login user
  async login(email: string, password: string): Promise<AuthResponse> {
    console.log('🚀 AuthAPI: Starting login request for:', email);
    
    try {
      console.log('📡 Making POST request to:', this.api.defaults.baseURL + '/auth/login');
      const response = await this.api.post('/auth/login', { email, password });
      
      console.log('✅ AuthAPI: Raw response received');
      console.log('📊 Status:', response.status);
      console.log('📦 Full response:', response);
      console.log('🎯 Response data:', response.data);
      console.log('🔍 Response data type:', typeof response.data);
      
      // The backend returns the auth data directly
      const authData = response.data;
      
      console.log('🔓 Processed auth data:', authData);
      console.log('🔑 Token present:', !!authData.token);
      console.log('👤 User present:', !!authData.user);
      console.log('⏰ Expires present:', !!authData.tokenExpires);
      
      // Validate required fields
      if (!authData.token) {
        console.error('❌ Missing token in response');
        throw new Error('No authentication token received from server');
      }
      
      if (!authData.user) {
        console.error('❌ Missing user data in response');
        throw new Error('No user data received from server');
      }
      
      console.log('👤 User role:', authData.user.role);
      console.log('📧 User email:', authData.user.email);
      
      // Store token, user data, and expiration
      console.log('💾 Storing auth data in localStorage...');
      localStorage.setItem('authToken', authData.token);
      localStorage.setItem('user', JSON.stringify(authData.user));
      if (authData.tokenExpires) {
        localStorage.setItem('tokenExpires', authData.tokenExpires);
      }
      
      console.log('✅ AuthAPI: Login successful, returning data');
      
      // Return the auth data directly
      return authData;
    } catch (error: any) {
      console.error('🚨 AuthAPI Login error details:');
      console.error('- Error object:', error);
      console.error('- Response status:', error.response?.status);
      console.error('- Response data:', error.response?.data);
      console.error('- Error message:', error.message);
      
      // Extract error message from various possible formats
      let errorMessage = 'Login failed';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        console.log('🔍 Parsing error data:', errorData);
        
        // Handle different error response formats
        if (errorData.error?.message) {
          errorMessage = errorData.error.message;
          console.log('📝 Using error.message:', errorMessage);
        } else if (errorData.message) {
          errorMessage = errorData.message;
          console.log('📝 Using message:', errorMessage);
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
          console.log('📝 Using string data:', errorMessage);
        }
      } else if (error.message) {
        errorMessage = error.message;
        console.log('📝 Using error.message:', errorMessage);
      }
      
      console.error('💥 Final error message:', errorMessage);
      throw new Error(errorMessage);
    }
  }

  // Get current user profile
  async getProfile(): Promise<User> {
    try {
      const response = await this.api.get('/auth/profile');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to get profile');
    }
  }

  // Update user profile
  async updateProfile(profileData: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    specialization?: string;
    licenseNumber?: string;
    hospital?: string;
    bio?: string;
  }): Promise<{ profile: any }> {
    try {
      const response = await this.api.put('/auth/update-profile', profileData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to update profile');
    }
  }

  // Update user password
  async updatePassword(passwordData: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ message: string }> {
    try {
      const response = await this.api.put('/auth/update-password', passwordData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to update password');
    }
  }

  // Password reset flow methods
  async forgotPassword(email: string): Promise<{ message: string; email: string }> {
    try {
      const response = await this.api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to send reset email');
    }
  }

  // Step 2: Verify reset token (NEW - Two-step verification)
  async verifyResetToken(email: string, resetToken: string): Promise<{ message: string; email: string; tokenValid: boolean }> {
    try {
      const response = await this.api.post('/auth/verify-reset-token', { email, resetToken });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Reset token verification failed');
    }
  }

  // Step 3: Reset password with verified token
  async resetPassword(email: string, resetToken: string, newPassword: string): Promise<{ message: string }> {
    try {
      const response = await this.api.post('/auth/reset-password', { email, resetToken, newPassword });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to reset password');
    }
  }

  // Legacy method for backward compatibility
  async resetPasswordLegacy(token: string, newPassword: string): Promise<{}> {
    try {
      const response = await this.api.post('/auth/reset-password', { token, newPassword });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to reset password');
    }
  }

  // Resend reset token (for the new two-step flow)
  async resendResetToken(email: string): Promise<{ message: string; email: string }> {
    try {
      const response = await this.api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to resend reset token');
    }
  }

  // Google OAuth: get the Google login URL
  getGoogleLoginUrl() {
    const baseURL = this.api.defaults.baseURL;
    return `${baseURL}/auth/google`;
  }

  // Google OAuth: handle callback
  async googleCallback(params: { code: string }) {
    try {
      const response = await this.api.post('/auth/google/callback', params);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Google authentication failed');
    }
  }

  // Logout
  async logout(): Promise<void> {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('tokenExpires');
  }

  // Get current user from localStorage
  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Check if token is expired (without clearing storage)
  isTokenExpired(): boolean {
    const tokenExpires = localStorage.getItem('tokenExpires');
    
    if (!tokenExpires) {
      return false; // No expiration date, assume not expired
    }
    
    try {
      const expirationDate = new Date(tokenExpires);
      const currentDate = new Date();
      return currentDate >= expirationDate;
    } catch (error) {
      console.error('Error parsing token expiration:', error);
      return false; // If we can't parse, assume not expired
    }
  }

  // Clear expired token if it's actually expired
  clearExpiredToken(): boolean {
    if (this.isTokenExpired()) {
      console.log('🧹 Clearing expired token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('tokenExpires');
      return true;
    }
    return false;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = localStorage.getItem('authToken');
    const tokenExpires = localStorage.getItem('tokenExpires');
    
    console.log('🔍 AuthAPI: Checking authentication...');
    console.log('- Token present:', !!token);
    console.log('- TokenExpires present:', !!tokenExpires);
    console.log('- TokenExpires value:', tokenExpires);
    
    if (!token) {
      console.log('❌ AuthAPI: No token found');
      return false;
    }
    
    // If no expiration date, assume token is valid
    if (!tokenExpires) {
      console.log('⚠️ AuthAPI: No expiration date, assuming token is valid');
      return true;
    }
    
    try {
      // Check if token is expired
      const expirationDate = new Date(tokenExpires);
      const currentDate = new Date();
      
      console.log('⏰ AuthAPI: Token expiration check');
      console.log('- Current date:', currentDate.toISOString());
      console.log('- Expiration date:', expirationDate.toISOString());
      console.log('- Is expired:', currentDate >= expirationDate);
      
      if (currentDate >= expirationDate) {
        console.log('❌ AuthAPI: Token expired, but not clearing storage here');
        // Token expired, but don't clear storage in isAuthenticated method
        // Let the auth context handle this more gracefully
        return false;
      }
      
      console.log('✅ AuthAPI: Token is valid');
      return true;
    } catch (error) {
      console.error('🚨 AuthAPI: Error parsing token expiration:', error);
      // If we can't parse the expiration date, assume token is valid
      return true;
    }
  }
}
